import { MsalProvider } from '@azure/msal-react';
import { useTranslation } from 'react-i18next';
import { Route, Routes } from 'react-router-dom';
import { AuthGate } from './auth/AuthGate';
import { CurrentUserProvider } from './auth/CurrentUserProvider';
import { msalInstance } from './auth/msalInstance';
import { DialogProvider } from './components/Dialog';
import { ErrorBoundary } from './components/ErrorBoundary';
import { Layout } from './components/Layout';
import { PageTitleProvider } from './components/PageTitle';
import { ToastProvider } from './components/Toast';
import { missingConfigKeys } from './config';
import { DashboardPage } from './pages/DashboardPage';
import { GroupAdminPage } from './pages/GroupAdminPage';
import { NotFoundPage } from './pages/NotFoundPage';
import { QrAuditPage } from './pages/QrAuditPage';
import { QrDetailPage } from './pages/QrDetailPage';
import { VCardPage } from './pages/VCardPage';
import { BulkEditPage } from './pages/BulkEditPage';
import { ThemeProvider } from './theme/ThemeProvider';

export function App() {
  const { t } = useTranslation();
  const missing = missingConfigKeys();

  if (missing.length > 0) {
    return (
      <div className="app-main">
        <div className="alert alert-danger d-flex gap-2" role="alert">
          <i className="pi pi-exclamation-triangle mt-1" aria-hidden="true" />
          <div>
            {t('app.notConfigured')}
            <ul className="mb-0 mt-1">
              {missing.map((key) => (
                <li key={key}>{key}</li>
              ))}
            </ul>
          </div>
        </div>
      </div>
    );
  }

  return (
    // Outermost, because the theme is not a property of being signed in: the login page has one too.
    <ThemeProvider>
      <MsalProvider instance={msalInstance}>
        <AuthGate>
          <CurrentUserProvider>
            <PageTitleProvider>
              <ToastProvider>
                <DialogProvider>
                  {/* Inside the providers, so the notice it falls back to can be translated and the rest of
                      the chrome stays on the page. */}
                  <ErrorBoundary>
                    <Routes>
                      <Route element={<Layout />}>
                        <Route path="/" element={<DashboardPage />} />
                        <Route path="/qr/:id" element={<QrDetailPage />} />
                        <Route path="/qr/:id/audit" element={<QrAuditPage />} />
                        <Route path="/vcard" element={<VCardPage />} />
                        <Route path="/groups" element={<GroupAdminPage />} />
                        <Route path="/bulk" element={<BulkEditPage />} />
                        <Route path="*" element={<NotFoundPage />} />
                      </Route>
                    </Routes>
                  </ErrorBoundary>
                </DialogProvider>
              </ToastProvider>
            </PageTitleProvider>
          </CurrentUserProvider>
        </AuthGate>
      </MsalProvider>
    </ThemeProvider>
  );
}
